import PopupBasic from 'src/game/components/popups/PopupBasic';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { AssetGroup } from '@play-co/timestep-core';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { BuddiesJoinRewards } from 'src/game/buddies/components/BuddiesJoinRewards';
import { BuddiesPlayerInfo } from 'src/game/buddies/components/BuddiesPlayerInfo';

type CloseHandler = (accepted: boolean) => void;

export default class PopupBuddiesIncomingInvite extends PopupBasic {
  private playerInfo: BuddiesPlayerInfo;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 700,
      darkerBg: false,
      close: () => {
        opts.close(false);
      },
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.POPUP_INFO,
      },
    });

    this.buttonClose.removeFromSuperview();

    // Content wrapper with FlexBox layout (same pattern as PopupUserProfile)
    const content = new FlexBoxView({
      superview: this.box,
      flex: {
        width: this.box.style.width,
        height: this.box.style.height,
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 80,
      },
    });

    // Message
    new LangBitmapFontTextView({
      superview: content,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      font: 'Body',
      color: '#BF8147',
      width: this.box.style.width - 80,
      height: 40,
      localeText: () => i18n('match2.buddies.incomingInvitePopup.subtitle'),
    });

    this.playerInfo = new BuddiesPlayerInfo({
      superview: content,
    });

    // Rewards section (ribbon + powerups + message)
    new BuddiesJoinRewards({
      superview: content,
      width: this.box.style.width,
    });

    // Button row
    const buttonWidth = 230;
    const buttonHeight = 90;
    const buttonMargin = 10;

    const buttonRow = new FlexBoxView({
      superview: content,
      flex: {
        width: this.box.style.width - 40,
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 15,
      },
    });

    const baseButtonOpts = {
      superview: buttonRow,
      width: buttonWidth,
      height: buttonHeight,
      labelOffsetX: -5,
      labelOffsetY: -1,
      fontSize: 38,
      font: 'Body' as const,
      centerOnOrigin: false,
    };

    // Cancel button
    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      ...baseButtonOpts,
      localeText: () => i18n('match2.buddies.incomingInvitePopup.cancel'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        opts.close(false);
      },
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.BUTTON_DECLINE,
      },
    });

    // Accept button
    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      ...baseButtonOpts,
      localeText: () => i18n('match2.buddies.incomingInvitePopup.accept'),
      onClick: async () => {
        sounds.sfx.play('ok');
        opts.close(true);
      },
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.BUTTON_ACCEPT,
      },
    });

    // Set margins for buttons
    for (const child of buttonRow.getSubviews()) {
      if (child) {
        getFlexNode(child).updateStyle({
          marginLeft: buttonMargin,
          marginRight: buttonMargin,
        });
      }
    }
  }

  protected getAssetsGroup() {
    return new AssetGroup(
      AssetGroup.constructURLs(['assets/ui/buddies/common']),
    );
  }

  public init(args: { name: string; photo: string }) {
    super.init({
      title: i18n('match2.buddies.incomingInvitePopup.title'),
    });
    this.playerInfo.setPlayer({ name: args.name, photo: args.photo });
  }
}
